import { OrderModel } from 'lib/model/objects/orderModel';
import { useEffect, useState } from 'react';
import { ConsolidatedOrderPricing } from 'lib/types/order';
import { logAndCaptureException } from 'utils';
import useDebounce from 'lib/frontend/hooks/useDebounce';
import { safeStringify } from 'lib/utils/stringify';
import { ColumnService } from 'lib/services/directory';
import { validateCompleteNewspaperOrder } from 'lib/types/newspaperOrder';
import { Ad } from 'lib/types/ad';
import { updateOrderPricing } from '../helpers/orderOperations';
import {
  NewspaperOrdersFormData,
  STALE_EDIT_SESSION_MESSAGE
} from '../PlacementFlowStepSelector';
import { hashString } from '../helpers/crypt';

const RECALCULATE_PRICE_DEBOUNCE_MS = 1000;

type usePricingProps = {
  orderModel: OrderModel | undefined;
  newspaperOrdersFormData: NewspaperOrdersFormData;
  adFormData: Partial<Ad>;
  version: number;
  persistModelsForPricing: () => Promise<void>;
  isEditing?: boolean;
};

export const usePricing = ({
  newspaperOrdersFormData,
  orderModel,
  adFormData,
  version,
  persistModelsForPricing,
  isEditing
}: usePricingProps) => {
  const [consolidatedOrderPricing, setConsolidatedOrderPricing] =
    useState<ConsolidatedOrderPricing>();
  const [priceIsStale, setPriceIsStale] = useState(true);
  const [orderDataCacheKey, setOrderDataCacheKey] = useState('');
  const [priceLoading, setPriceLoading] = useState(false);
  const [error, setError] = useState('');
  const [oldTotalInCents, setOldTotalInCents] = useState<number | null>(null);
  const [refundRequestedInCents, setRefundRequestedInCents] = useState(0);
  const [isPartialRefund, setIsPartialRefund] = useState(false);
  const [previousInvoiceRefundValid, setPreviousInvoiceRefundValid] =
    useState(false);

  const maxHeightExceeded =
    consolidatedOrderPricing?.newspaperOrderPublishingDataGroup.some(
      newspaperOrder => newspaperOrder.displayParams?.maxHeightExceeded
    );
  const layoutError = maxHeightExceeded
    ? 'Maximum length exceeded for one or more layouts. Please edit the content to continue.'
    : undefined;

  // We're returning the price here to get it to callers immediately so they don't need to wait for state change
  const handleRefreshOrderPrice = async (): Promise<
    ConsolidatedOrderPricing | undefined
  > => {
    setPriceLoading(true);
    if (!orderModel) {
      return;
    }
    await orderModel.refreshData();
    const consolidatedPricing = await orderModel.getConsolidatedPricing({
      distributeFee: true,
      version
    });
    setConsolidatedOrderPricing(consolidatedPricing);
    // Up-to-date local variables to avoid state-set timing gaps
    let localOldTotal: number | null = oldTotalInCents;
    let localPreviousInvoiceRefundValid = previousInvoiceRefundValid;

    // If editing capture original total and prior invoice status (only once)
    if (isEditing && oldTotalInCents === null) {
      const [oldPricingError, oldPricing] = await orderModel.getOrderPricing(
        orderModel.modelData.activeVersion
      );
      if (!oldPricingError && oldPricing) {
        localOldTotal = oldPricing.totalInCents;
        setOldTotalInCents(oldPricing.totalInCents);
      }
      const [invoiceError, invoice] = await orderModel.getInvoice(
        orderModel.modelData.activeVersion
      );
      if (!invoiceError && invoice) {
        localPreviousInvoiceRefundValid =
          (invoice.paymentReceived() || invoice.paymentPending()) &&
          !invoice.isPaidDirectToPublisher();
        setPreviousInvoiceRefundValid(localPreviousInvoiceRefundValid);
      }
    }

    if (localOldTotal !== null && consolidatedPricing) {
      const diff = localOldTotal - consolidatedPricing.totalInCents;
      const shouldRefund = diff > 0 && localPreviousInvoiceRefundValid;
      setIsPartialRefund(shouldRefund);
      setRefundRequestedInCents(shouldRefund ? diff : 0);
    }
    setPriceLoading(false);
    return consolidatedPricing;
  };

  // We're returning the price here to get it to callers immediately so they don't need to wait for state change
  const handleRecalculateOrderPrice = async (): Promise<
    ConsolidatedOrderPricing | undefined
  > => {
    if (!orderModel) {
      return;
    }
    try {
      setError('');
      setPriceLoading(true);

      await persistModelsForPricing();

      await updateOrderPricing(orderModel.id, version);
      const consolidatedPricing = await handleRefreshOrderPrice();
      setPriceIsStale(false);
      return consolidatedPricing;
    } catch (e: any) {
      if (e.message !== STALE_EDIT_SESSION_MESSAGE) {
        setError('Failed to recalculate order price.  Please try again later.');
        logAndCaptureException(
          ColumnService.ORDER_PLACEMENT,
          e,
          'Failed to recalculate order price',
          {
            orderId: orderModel.id
          }
        );
      }
    } finally {
      setPriceLoading(false);
    }
  };

  /**
   * Update the priceIsStale state when the form data changes and set a "cache key" that will be used to debounce
   * the recalculation of the order price.
   * We re-calculte the cache key whenever we change text, images or our layout.
   */
  useEffect(() => {
    if (!priceIsStale) {
      setPriceIsStale(true);
    }
    void (async () => {
      if (newspaperOrdersFormData.length === 0) {
        return;
      }
      for (const newspaperOrder of newspaperOrdersFormData) {
        if (!validateCompleteNewspaperOrder(newspaperOrder)) {
          return;
        }
      }
      const orderCacheKey =
        (await hashString(safeStringify(newspaperOrdersFormData) || '')) +
        (await hashString(safeStringify(adFormData) || ''));
      setOrderDataCacheKey(orderCacheKey);
    })();
  }, [
    safeStringify(newspaperOrdersFormData),
    safeStringify(adFormData),
    adFormData.filingTypeName
  ]);

  const debouncedCacheKey = useDebounce(
    orderDataCacheKey,
    RECALCULATE_PRICE_DEBOUNCE_MS
  );
  useEffect(() => {
    if (!debouncedCacheKey) {
      return;
    }
    void handleRecalculateOrderPrice();
  }, [orderModel?.id, debouncedCacheKey]);

  return {
    priceIsStale,
    consolidatedOrderPricing,
    priceLoading,
    handleRecalculateOrderPrice,
    handleRefreshOrderPrice,
    priceError: error || layoutError,
    oldTotalInCents,
    isPartialRefund,
    refundRequestedInCents
  };
};
